import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { Button, Icon, Modal, Table } from '@components/index';
import {
  AccountType,
  DEFAULT_PLACEHOLDER,
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT
} from '@constants/generic';
import {
  defaultTemplateHeaders,
  downloadTemplateOptions,
  templateDummayData
} from '@features/group-management/constants';
import { UserProfileCustomField } from '@features/member-management/types';
import useActiveNotificationChannel from '@hooks/useActiveNotificationChannel';
import { formatISODate } from '@utils/date';
import createExcelWithDropdowns from '@utils/exportExcel';
import { addKeyValue } from '@utils/generic';
import { capitalizeFirstLetter } from '@utils/stringFormat';

import theme from '../../../../../../../themes/colors';

interface DownloadAndViewTemplateProps {
  isFileAvailable: boolean;
  tableHeaders: string[];
  tableRows: any;
  feeCategories: string[];
  isOnce: boolean;
  isCustomTerm: boolean;
  termsName: string[];
  isLeadSquared: boolean;
  isEndDateEnabled: boolean;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
}

// To fix type issue with Worksheet in exceljs
declare module 'exceljs' {
  export interface DataValidations {
    add(range: string, validation: DataValidation): void;
    readonly model: Record<string, DataValidation>;
  }

  export interface Worksheet {
    dataValidations: DataValidations;
  }
}

const DownloadAndViewTemplate = ({
  isFileAvailable,
  tableHeaders,
  tableRows,
  feeCategories,
  isOnce,
  isCustomTerm,
  termsName,
  isLeadSquared,
  isEndDateEnabled,
  isAttendanceBasedFee = false,
  isSessionBased = false
}: DownloadAndViewTemplateProps) => {
  const { groupId } = useParams();

  const { orgConfig, accountType } = useSelector((state: any) => state.user);
  const { isNotificationChannelActive } = useActiveNotificationChannel();

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDownloadTemplateModalOpen, setIsDownloadTemplateModalOpen] =
    useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<string>('Basic Template');

  const isEmailEnabled = isNotificationChannelActive(
    'payment_request',
    'email'
  );
  const isAmountPaidTillDateEnabled =
    !!orgConfig?.is_amount_paid_till_date_enabled && !groupId;
  const customFields =
    orgConfig?.member_custom_fields as UserProfileCustomField[];

  const getMonthDayLabel = (day: string): string => {
    if (day && !Number.isNaN(Number(day))) {
      const n = Number(day);
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      const suffix = s[(v - 20) % 10] || s[v] || s[0];
      return `${n}${suffix} day of the month`;
    }
    return day;
  };

  const getBillingColumnKey = () => {
    if (!isAttendanceBasedFee) return '';
    return isSessionBased ? 'billingPeriod(numberOfClasses)' : 'startDate';
  };

  const formatBillingValue = (value: any) => {
    if (!value) return DEFAULT_PLACEHOLDER;
    if (isSessionBased) return `${value} classes`;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return getMonthDayLabel(`${parsed.getDate()}`);
    }
    return getMonthDayLabel(`${value}`);
  };

  const getCategoryValues = (row: any) => {
    const categoryValues: { [key: string]: any } = {};
    feeCategories.forEach((category) => {
      categoryValues[category] = row[category] || DEFAULT_PLACEHOLDER;
    });
    return categoryValues;
  };

  const rowData = tableRows?.map((row: any, index: number) => ({
    ...{
      id: `${index}_${row.name}`,
      name: row?.name || DEFAULT_PLACEHOLDER,
      primaryNumber: row?.primaryNumber
        ? `+${row?.primaryNumberCode} ${row?.primaryNumber
            .toString()
            .replace(/\s+/g, '')}`
        : DEFAULT_PLACEHOLDER,
      altNumber: row?.altNumber
        ? `+${row?.altNumberCode} ${row?.altNumber
            .toString()
            .replace(/\s+/g, '')}`
        : DEFAULT_PLACEHOLDER,
      email: row?.email || DEFAULT_PLACEHOLDER,
      amount: row.amount ? (
        <div className="flex items-center">
          <CurrencyAmount
            value={Number(row.amount)}
            className="gap-0.5"
            symbolClass=""
            valueClass=""
          />
        </div>
      ) : (
        DEFAULT_PLACEHOLDER
      ),
      amountPaid: row.amountPaid ? (
        <div className="flex items-center">
          <CurrencyAmount
            value={Number(row.amountPaid)}
            className="gap-0.5"
            symbolClass=""
            valueClass=""
          />
        </div>
      ) : (
        DEFAULT_PLACEHOLDER
      ),
      frequency: row?.frequency || DEFAULT_PLACEHOLDER,
      monthsPerTerm:
        row?.frequency === 'By term' ? row?.monthsPerTerm : DEFAULT_PLACEHOLDER,
      startDate: row?.startDate
        ? formatISODate({ date: row?.startDate })
        : DEFAULT_PLACEHOLDER,
      admissionNumber: row?.admissionNumber || DEFAULT_PLACEHOLDER,
      admissionDate: row?.admissionDate || DEFAULT_PLACEHOLDER,
      dateOfBirth: row?.dateOfBirth || DEFAULT_PLACEHOLDER,
      guardianName: row?.guardianName || DEFAULT_PLACEHOLDER,
      addressLine1: row?.addressLine1 || DEFAULT_PLACEHOLDER,
      addressLine2: row?.addressLine2 || DEFAULT_PLACEHOLDER,
      country: row?.country || DEFAULT_PLACEHOLDER,
      state: row?.state || DEFAULT_PLACEHOLDER,
      city: row?.city || DEFAULT_PLACEHOLDER,
      pincode: row?.pincode || DEFAULT_PLACEHOLDER,
      age: row?.age || DEFAULT_PLACEHOLDER,
      bloodGroup: row?.bloodGroup || DEFAULT_PLACEHOLDER,
      isInsured: row?.isInsured || DEFAULT_PLACEHOLDER,
      counsellorName: row?.counsellorName || DEFAULT_PLACEHOLDER,
      ...(isLeadSquared
        ? { courseFee: row?.courseFee || DEFAULT_PLACEHOLDER }
        : {}),
      ...(isEndDateEnabled
        ? {
            endDate: row?.endDate
              ? formatISODate({ date: row?.endDate })
              : DEFAULT_PLACEHOLDER
          }
        : {}),
      ...(isAttendanceBasedFee
        ? {
            ratePerClass: row?.ratePerClass ? (
              <div className="flex items-center">
                <CurrencyAmount
                  value={Number(row.ratePerClass)}
                  className="gap-0.5"
                  symbolClass=""
                  valueClass=""
                />
              </div>
            ) : (
              DEFAULT_PLACEHOLDER
            ),
            [getBillingColumnKey()]: formatBillingValue(
              row?.[getBillingColumnKey()]
            )
          }
        : {})
    },
    ...(feeCategories.length > 0 ? getCategoryValues(row) : {}),

    ...Object.keys(row)
      .filter((key) => key.startsWith('Term'))
      .reduce((acc, term) => {
        acc[term] = row[term] || DEFAULT_PLACEHOLDER;
        return acc;
      }, {} as Record<string, any>),

    // Handle custom terms if applicable
    ...(isCustomTerm
      ? Object.fromEntries(
          termsName.map((term) => [term, row[term] || DEFAULT_PLACEHOLDER])
        )
      : {})
  }));

  const columnData = tableHeaders?.map((columnHeader: string) => {
    let headerText = columnHeader;

    if (columnHeader === 'primaryNumber') {
      headerText = 'Primary Number';
    } else if (columnHeader === 'altNumber') {
      headerText = 'Alternate Number';
    } else if (columnHeader === 'monthsPerTerm') {
      headerText = 'Months per Term';
    } else if (columnHeader === 'amountPaid') {
      headerText = 'Amount Paid';
    } else if (columnHeader === 'startDate') {
      headerText = isAttendanceBasedFee ? 'Billing Period' : 'Start Date';
    } else if (columnHeader === 'admissionDate') {
      headerText = 'Admission Date';
    } else if (columnHeader === 'dateOfBirth') {
      headerText = 'Date of Birth';
    } else if (columnHeader === 'guardianName') {
      headerText = 'Guardian Name';
    } else if (columnHeader === 'addressLine1') {
      headerText = 'Address Line 1';
    } else if (columnHeader === 'addressLine2') {
      headerText = 'Address Line 2';
    } else if (columnHeader === 'endDate') {
      headerText = 'End Date';
    } else if (columnHeader === 'courseFee') {
      headerText = 'Course Fee';
    } else if (columnHeader === 'counsellorName') {
      headerText = 'Counsellor Name';
    } else if (
      columnHeader === 'ratePerClass' ||
      columnHeader === 'ratePerSession'
    ) {
      headerText = 'Rate per class';
    } else if (
      columnHeader === 'billingPeriod' ||
      columnHeader === 'billingPeriod(numberOfClasses)' ||
      columnHeader === 'sessionsPerCycle'
    ) {
      headerText = 'Billing Period';
    }

    return {
      Header: capitalizeFirstLetter(headerText),
      accessor: columnHeader
    };
  });

  const styledColumnData = addKeyValue(
    columnData.filter(
      (column) =>
        column.accessor !== 'primaryNumberCode' &&
        column.accessor !== 'altNumberCode'
    ),
    'name',
    'style',
    '!font-medium'
  );

  const onDownloadTemplate = (isUserProfileInclude = false) => {
    const headers = defaultTemplateHeaders(
      feeCategories,
      isEmailEnabled,
      isAmountPaidTillDateEnabled,
      isOnce,
      customFields,
      termsName,
      isUserProfileInclude,
      isLeadSquared,
      isCustomTerm,
      isEndDateEnabled,
      isAttendanceBasedFee,
      isSessionBased
    );

    const baseData = templateDummayData(
      feeCategories.length > 0,
      isEmailEnabled,
      isAmountPaidTillDateEnabled,
      isOnce,
      customFields,
      isUserProfileInclude,
      isCustomTerm,
      isLeadSquared,
      isEndDateEnabled,
      isAttendanceBasedFee,
      isSessionBased
    );

    // if (isUserProfileInclude) {
    const feeRepeat = new Array(feeCategories.length).fill(100);
    const excelData = baseData.concat(feeRepeat);
    createExcelWithDropdowns(
      headers,
      excelData,
      groupMemberMinAmount,
      groupMemberMaxAmount
    );
    // } else {
    //   const csvFileHeaders = `${headers}\n`;
    //   const feeRepeat = ',100'.repeat(feeCategories.length);
    //   const csvData = baseData + feeRepeat;
    //   exportCsvFromData(csvFileHeaders.concat(csvData), 'template');
    // }
  };

  const downloadTemplateLabel =
    accountType.type === AccountType.ORGANIZATION
      ? 'Download Template'
      : 'Download Basic Template';

  const downloadTemplate = () => {
    if (accountType.type === AccountType.ORGANIZATION)
      setIsDownloadTemplateModalOpen(true);
    else onDownloadTemplate(false);
  };

  const handleDownload = () => {
    if (selectedTemplate === 'Basic Template') onDownloadTemplate(false);
    else onDownloadTemplate(true);
    setIsDownloadTemplateModalOpen(false);
  };

  const onViewFile = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="mb-5 flex">
        {isFileAvailable ? (
          <div
            className="flex cursor-pointer items-center gap-2 py-1.5"
            role="presentation"
            onClick={onViewFile}
          >
            <Icon name="eye" stroke={theme.grey} />
            <span className="text-sm font-medium text-grey">View File</span>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div
              className="flex cursor-pointer items-center gap-2 py-1.5 pr-4"
              role="presentation"
              onClick={downloadTemplate}
            >
              <Icon name="download" stroke={theme.primary} />
              <span className="text-sm font-medium text-primary">
                {downloadTemplateLabel}
              </span>
            </div>
          </div>
        )}
      </div>
      <Modal
        isOpen={isDownloadTemplateModalOpen}
        onCancel={() => setIsDownloadTemplateModalOpen(false)}
        isCloseIconRequired
        className="w-[450px]"
      >
        <p className="border-b px-6 py-4 text-lg font-medium">
          Download Template
        </p>
        <div className="space-y-4 border-b px-6 py-4">
          {downloadTemplateOptions.map((option) => (
            <div key={option.value} className="flex items-start gap-3">
              <input
                type="radio"
                id={option.value}
                name="template"
                value={option.value}
                checked={selectedTemplate === option.value}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="mt-4 accent-green-700"
              />
              <label htmlFor={option.value} className="cursor-pointer">
                <div className="font-medium">{option.label}</div>
                <div className="text-sm font-light text-grey">
                  {option.subtext}
                </div>
              </label>
            </div>
          ))}
        </div>
        <div className="px-6 py-4">
          <Button
            label="Download"
            size="full"
            variant="primary"
            className="h-[44px]"
            handleClick={handleDownload}
          />
        </div>
      </Modal>
      <Modal isOpen={isModalOpen} onCancel={closeModal} isCloseIconRequired>
        <div className="px-4 pb-4 pt-8">
          <div className="flex justify-between">
            <div className="text-xl font-medium">Preview</div>
          </div>
          <div className="mt-26 h-[calc(100vh-500px)] w-200 rounded-lg bg-white p-4">
            <Table
              columns={styledColumnData}
              data={rowData}
              rowDataStyle="!py-3.6"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DownloadAndViewTemplate;
